'use client';

import { useEffect, useRef, useState } from 'react';

type UseHardcoreTimerParams = {
  /** Le timer tourne uniquement quand `active` est vrai. */
  active: boolean;
  /**
   * Échéance absolue (ms epoch) issue de la machine, persistée avec la partie.
   * `null` tant que le timer n'est pas armé.
   */
  deadline: number | null;
  /** Appelé une seule fois quand l'échéance est atteinte/dépassée. */
  onExpire: () => void;
};

/**
 * Décompte hardcore basé sur une échéance ABSOLUE (deadline). Comme la deadline
 * est persistée dans la partie (machine + sauvegarde), le temps continue de
 * s'écouler même après un reload ou un changement de jour : impossible de
 * remettre le compteur à zéro (anti-triche). Si l'échéance est déjà dépassée au
 * retour, `onExpire` est appelé immédiatement → défaite.
 */
export function useHardcoreTimer({
  active,
  deadline,
  onExpire,
}: UseHardcoreTimerParams) {
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Nouvelle échéance (nouvelle partie / autre jour) → on réarme.
  useEffect(() => {
    expiredRef.current = false;
  }, [deadline]);

  useEffect(() => {
    if (!active || deadline == null) {
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, deadline - Date.now());
      setRemainingMs(remaining);

      if (remaining <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpireRef.current();
      }
    };

    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [active, deadline]);

  const remainingSeconds =
    remainingMs === null ? null : Math.ceil(remainingMs / 1000);

  return { remainingMs, remainingSeconds };
}
